// Los motores de imagen que se pueden ELEGIR (`provider_override` de `/v1/ads/generate`).
// Espejo de `IMAGE_PROVIDER_OVERRIDES` en atlas-backend/src/providers/imageChain.ts — el
// backend valida el id con su zod.
//
// Un solo catálogo para los dos lugares que lo ofrecen:
//   • el harness de test masivo (el select "Proveedor" del editor de sets y el resumen del
//     lightbox), donde el elegido corre SOLO, sin fallback: es lo que está midiendo;
//   • "Opciones de modelo avanzadas", adentro del menú del "+" del Agente
//     (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario), abierto a cualquier cuenta
//     que tenga el Agente, donde el elegido va PRIMERO y la cadena de siempre queda atrás.
// Quién recibe cuál lo decide el backend con el `dev_tester` de la empresa; el front no
// manda esa diferencia.
//
// Sin proveedor (null) corre la cadena de producción: Gemini, y si falla GPT Image 2.5
// Sunburst o Seedream (ADR-2026-09-14-sunburst-es-el-openai-de-la-cadena-de-creacion).
//
// **Los `detalle` los LEE UN CLIENTE.** Describen para qué es bueno cada motor, no qué
// lugar ocupa en nuestra cadena: decían "el primario de producción" y "el fallback", que
// además de filtrar cómo generamos dejó de ser cierto en cuanto el elegido pasó a correr
// con red. Lo que la cadena hace sola se cuenta una vez, en la opción "Atlas".

export type ProveedorDeImagen =
  | 'gemini'
  | 'gpt-image-2.5-flare'
  | 'gpt-image-2.5-sunburst'
  | 'openai'
  | 'seedream'
  | 'recraft'
  | 'nano-banana-2.1';

export interface OpcionProveedorDeImagen {
  id: ProveedorDeImagen;
  label: string;
  /** Una línea: qué modelo es y para qué sirve. */
  detalle: string;
}

/** En el orden en que se muestran, del más general al más específico. */
export const PROVEEDORES_DE_IMAGEN: readonly OpcionProveedorDeImagen[] = [
  {
    id: 'gemini',
    label: 'Gemini 3.1 Flash Image',
    detalle: 'Rápido y parejo. El más sólido para crear una escena de cero.',
  },
  {
    id: 'nano-banana-2.1',
    label: 'Nano Banana 2.1',
    detalle: 'El Gemini nuevo (octubre 2026): más realismo, mejor texto y hasta 14 referencias. Misma velocidad, la mitad del costo.',
  },
  {
    id: 'gpt-image-2.5-sunburst',
    label: 'GPT Image 2.5 Sunburst',
    detalle: 'El más preciso para EDITAR: cambiar un color, un fondo o una pose sin tocar el resto. Tarda más.',
  },
  {
    id: 'gpt-image-2.5-flare',
    label: 'GPT Image 2.5 Flare',
    detalle: 'El hermano rápido del anterior. Menos control, menos espera.',
  },
  {
    id: 'openai',
    label: 'GPT-5',
    detalle: 'El mejor con texto adentro de la imagen: carteles, precios, placas.',
  },
  {
    id: 'seedream',
    label: 'Seedream 5.0 pro',
    detalle: 'El más permisivo: si otro te rebota una foto por sus reglas de contenido, probá con este.',
  },
  {
    id: 'recraft',
    label: 'Recraft V4.1',
    detalle: 'Ideas, escenas de cine, caras inventadas y gráfica. Las fotos que recibe son sólo de estilo.',
  },
];

const IDS = new Set<string>(PROVEEDORES_DE_IMAGEN.map((p) => p.id));

export function esProveedorDeImagen(v: unknown): v is ProveedorDeImagen {
  return typeof v === 'string' && IDS.has(v);
}

export const ETIQUETA_CADENA_NORMAL = 'Cadena normal (con fallback)';

/** Etiqueta para mostrar; sin proveedor, la cadena de producción. */
export function etiquetaProveedorDeImagen(id: ProveedorDeImagen | null | undefined): string {
  if (!id) return ETIQUETA_CADENA_NORMAL;
  return PROVEEDORES_DE_IMAGEN.find((p) => p.id === id)?.label ?? id;
}

/**
 * La opción por defecto del selector del Agente: dejar que elijamos nosotros.
 *
 * Se llama **Atlas** y no "Automático" (pedido de Matías,
 * ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario): "Automático" en una lista de
 * marcas ajenas se lee como una sexta opción rara, mientras que Atlas es la respuesta a
 * "¿quién elige?" y además es la única entrada de la lista que es NUESTRA.
 *
 * **Su detalle no nombra a los otros motores, a propósito.** El default hace exactamente
 * eso —corre Gemini y si falla sigue con OpenAI o Seedream— pero contarlo acá convierte
 * cada generación normal en una lista de proveedores de terceros. Lo que el usuario
 * necesita saber es quién decide y con qué criterio.
 *
 * Vive en este módulo y no en el componente porque es parte del catálogo: el día que la
 * cadena cambie, este texto se revisa junto a los otros cinco y no en un archivo de JSX.
 */
export const MOTOR_AUTOMATICO = {
  label: 'Atlas',
  detalle: 'La mejor opción para cualquier tipo de imagen profesional.',
} as const;

/**
 * Los motores que NO se ofrecen en el Agente, aunque el backend los acepte y el harness los
 * necesite.
 *
 * `openai` es el gpt-5 con el tool de imagen: es el primario de los modos anuncio y una
 * comparación válida en el test masivo, pero como opción suelta en el menú de un cliente no
 * tiene lugar — pedido de Matías. Sale de la LISTA, no del catálogo: `PROVEEDORES_DE_IMAGEN`
 * sigue siendo el espejo de `IMAGE_PROVIDER_OVERRIDES` del backend, y borrarlo de ahí dejaría
 * al harness sin poder probarlo.
 *
 * `recraft` tampoco: toma las fotos sólo como estilo, así que elegido como preferencia
 * iría primero también cuando hay un producto o una persona real, y los perdería. Entra
 * solo, por la tarea `explorar` (ADR-2026-10-04-recraft-entra-por-la-tarea-explorar); acá
 * queda para el harness.
 *
 * `nano-banana-2.1` tampoco, por ahora: es el sucesor de Gemini 3.1 según Google y entró
 * para COMPARARLO en el harness (ADR-2026-10-07-nano-banana-2-1-en-el-test-masivo). Si la
 * comparación lo confirma, lo que corresponde es que reemplace a 3.1 en la cadena y en la
 * opción "Gemini" de este menú, no sumar una segunda entrada de Google al cliente.
 */
const FUERA_DEL_AGENTE: ReadonlySet<ProveedorDeImagen> = new Set(['openai', 'recraft', 'nano-banana-2.1']);

/** La lista completa como se ve en el Agente: Atlas primero, después los motores. */
export const OPCIONES_DE_MOTOR: ReadonlyArray<{
  id: ProveedorDeImagen | null;
  label: string;
  detalle: string;
}> = [
  { id: null, ...MOTOR_AUTOMATICO },
  ...PROVEEDORES_DE_IMAGEN.filter((p) => !FUERA_DEL_AGENTE.has(p.id)),
];

const IDS_DEL_AGENTE = new Set<string>(
  OPCIONES_DE_MOTOR.map((o) => o.id).filter((id): id is ProveedorDeImagen => id !== null),
);

/**
 * ¿Es un motor que el Agente OFRECE hoy? Es más angosto que `esProveedorDeImagen`, que
 * valida contra el catálogo entero (el espejo del backend, con `openai` adentro para el
 * harness).
 *
 * Lo usa la elección GUARDADA del usuario. Sin esto, quien había elegido un motor antes de
 * que saliera de la lista se quedaba con lo peor de los dos mundos: el selector no le
 * marcaba ninguna opción —el suyo ya no está— pero cada imagen que generaba seguía saliendo
 * por ese `provider_override`, sin forma de verlo ni de cambiarlo salvo eligiendo otro. Un
 * motor que deja de ofrecerse vuelve a Atlas, que es lo que el selector muestra.
 */
export function esMotorDelAgente(v: unknown): v is ProveedorDeImagen {
  return typeof v === 'string' && IDS_DEL_AGENTE.has(v);
}
